import { Identifier, StructurePlacement, StructureSet, WorldgenStructure } from "deepslate";
import { defineStore } from "pinia";
import { computed, reactive, ref, watchEffect } from "vue";

const STORAGE_KEY = "mc-datapack-map.search-state.v1"

type StoredSearchState = {
    biomes?: unknown
    structures?: unknown
    disabled?: unknown
}

function loadSearchState(): StoredSearchState {
    try {
        const stored = localStorage.getItem(STORAGE_KEY)
        if (stored === null) return {}

        const parsed = JSON.parse(stored)
        return parsed !== null && typeof parsed === "object" ? parsed : {}
    } catch {
        return {}
    }
}

function storedSet(value: unknown): Set<string> {
    if (!Array.isArray(value)) return new Set()
    return new Set(value.filter((id): id is string => typeof id === "string"))
}

export const useSearchStore = defineStore('search', () => {
    const stored = loadSearchState()
    const biomes = reactive<Set<string>>(storedSet(stored.biomes))
    const structures = reactive<Set<string>>(storedSet(stored.structures))

    const disabled = ref(stored.disabled === true)

    watchEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify({
                biomes: [...biomes],
                structures: [...structures],
                disabled: disabled.value
            }))
        } catch {
            // Storage can be unavailable in restricted browser contexts.
        }
    })

    const structure_sets = computed(() => {
        structures.size // make sure any change to structures triggers recomputation
        const sets: Identifier[] = []

        var has_invalid_set = false
        for (const id of StructureSet.REGISTRY.keys()){
            const set = StructureSet.REGISTRY.get(id)
            if (!set) continue

            var set_needed = false
            var set_invalid = false

            for (const entry of set?.structures){
                if (structures.has(entry.structure.key()?.toString() ?? "<>")){
                    set_needed = true
                    const s = entry.structure.value()
                    if (s instanceof WorldgenStructure.MineshaftStructure || s instanceof WorldgenStructure.NetherFossilStructure || s instanceof WorldgenStructure.OceanMonumentStructure || s instanceof WorldgenStructure.RuinedPortalStructure){
                        set_invalid = true
                    }
                }
            }
            if (set_needed){
                if (set_invalid){
                    has_invalid_set = true
                } else {
                    sets.push(id)
                }
            }
        }
        return {sets, has_invalid: has_invalid_set}
    })

    return {biomes, structures, structure_sets, disabled}
})
